import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useNavState } from '@/providers/NavStateProvider';
import { useEffect } from 'react';
import { useLoading } from '@/providers/LoadingProvider';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useUpdateTeam } from '@/hooks/api/0200_update_team';
import { toastSuccess } from '@/lib/toast';
import { useInvalidateTeams } from '@/hooks/api/0180_teams';
import { useCurrentTeam } from '@/providers/TeamProvider';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { useUpdateTaskPhaseSkipSetting } from '@/hooks/api/2540_update_task_phase_skip_setting';

type FormInputs = {
  identifier: string;
  team_id: number;
  quotation_task_skippable: boolean;
  sales_order_contract_task_skippable: boolean;
  invoice_task_skippable: boolean;
  purchase_order_contract_task_skippable: boolean;
  payment_task_skippable: boolean;
};

export const TeamTaskPhaseSettingEdit = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('タスクスキップ設定'), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `チーム設定`, path: `/w/${_w_!.identifier}/setting/teams` },
        { label: `チーム詳細`, path: `/w/${_w_!.identifier}/setting/teams/${_t_!.id}` },
      ]),
    []
  );

  const navigate = useNavigate();
  const { team_id } = useParams();
  const { setShowLoading } = useLoading();
  const { _w_, me, _t_ } = useCurrentTeam();
  const { mutate, data, isPending, error } = useUpdateTaskPhaseSkipSetting();
  const { invalidate } = useInvalidateTeams(_w_!.identifier);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: Number(team_id!),
      quotation_task_skippable: _t_!.task_phase_skip_setting.quotation_task_skippable,
      sales_order_contract_task_skippable: _t_!.task_phase_skip_setting.sales_order_contract_task_skippable,
      invoice_task_skippable: _t_!.task_phase_skip_setting.invoice_task_skippable,
      purchase_order_contract_task_skippable: _t_!.task_phase_skip_setting.purchase_order_contract_task_skippable,
      payment_task_skippable: _t_!.task_phase_skip_setting.payment_task_skippable,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('タスクスキップ設定を編集しました');
      invalidate(); // Teamsのキャッシュクリア
      navigate(-1);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  if (_t_ == null) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'team'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">タスクスキップ設定</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0">
                  <ApiErrorMessages error={error} />

                  <div className="form-group">
                    <label>見積申請タスクのスキップ</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-quotation-task-skippable"
                        className="form-check-input"
                        {...register('quotation_task_skippable')}
                      />
                      <label className="form-check-label" htmlFor="on-quotation-task-skippable">
                        見積申請タスクのフェーズを「申請準備」と「完了」のみにする
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>契約申請（売上）タスクのスキップ</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-sales-order-contract-task-skippable"
                        className="form-check-input"
                        {...register('sales_order_contract_task_skippable')}
                      />
                      <label className="form-check-label" htmlFor="on-sales-order-contract-task-skippable">
                        契約申請（売上）タスクのフェーズを「申請準備」と「完了」のみにする
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>請求タスクのスキップ</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-invoice-task-skippable"
                        className="form-check-input"
                        {...register('invoice_task_skippable')}
                      />
                      <label className="form-check-label" htmlFor="on-invoice-task-skippable">
                        請求タスクのフェーズを「請求書準備」と「対応終了」のみにする
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>契約申請（費用）タスクのスキップ</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-purchase-order-contract-task-skippable"
                        className="form-check-input"
                        {...register('purchase_order_contract_task_skippable')}
                      />
                      <label className="form-check-label" htmlFor="on-purchase-order-contract-task-skippable">
                        契約申請（費用）タスクのフェーズを「申請準備」と「完了」のみにする
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>支払タスクのスキップ</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-payment-task-skippable"
                        className="form-check-input"
                        {...register('payment_task_skippable')}
                      />
                      <label className="form-check-label" htmlFor="on-payment-task-skippable">
                        支払タスクのフェーズを「請求書受領」と「対応終了」のみにする
                      </label>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                    戻る
                  </Link>
                  <button type="submit" className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
